<template>
    <div class="roles-page">
        <!-- Header -->
        <div class="roles-header">
            <div class="roles-header-left">
                <div class="roles-icon-wrap"><i class="fas fa-user-tag"></i></div>
                <h2 class="roles-title">User Roles</h2>
                <span class="roles-count" v-if="userRoles.length">{{ userRoles.length }}</span>
            </div>
            <button class="roles-add-btn" @click="showForm = true">
                <i class="fas fa-plus"></i>
                Add Role
            </button>
        </div>

        <!-- Create / Edit Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Role' : 'New Role'" @close="closeForm">
            <Form
                :fields="createFields"
                title="User Roles"
                submitUrl="/user-roles"
                updateRootUrl="/user-roles"
                @success="handleFormSuccess"
                @cancel="closeForm"
                :editMode="editMode"
                :editData="editData"
            />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="roles-loading">
            <div class="roles-spinner"></div>
            <span>Loading roles...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!userRoles.length" class="roles-empty">
            <div class="roles-empty-icon"><i class="fas fa-user-tag"></i></div>
            <h3>No roles found</h3>
            <p>Create a role to get started</p>
        </div>

        <!-- Cards Grid -->
        <div v-else class="roles-grid">
            <div
                class="role-card"
                v-for="(role, idx) in userRoles"
                :key="role.id"
                :style="{ animationDelay: `${idx * 0.03}s` }"
            >
                <div class="role-card-icon" :class="getRoleColor(idx)">
                    <i :class="getRoleIcon(role.name)"></i>
                </div>
                <div class="role-card-body">
                    <h4 class="role-card-name">{{ role.name }}</h4>
                    <span class="role-card-id">ID: {{ role.id }}</span>
                </div>
                <div class="role-card-actions">
                    <button class="role-action-btn role-btn-edit" @click="enableEditing(role)">
                        <i class="fas fa-pen"></i>
                    </button>
                    <button class="role-action-btn role-btn-delete" @click="deleteRole(role.id)">
                        <i class="fas fa-trash-alt"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import Form from "@/components/crud/form.vue"
import RightSideModal from "@/components/layout/RightSideModal.vue"
import { createField } from "@/utils/common"

export default {
    data() {
        return {
            loading: true,
            showForm: false,
            editMode: false,
            editData: null,
            userRoles: [],
            createFields: [createField("Name*", "name", "text", true)],
        }
    },
    components: { Form, RightSideModal },
    async mounted() {
        await this.fetchRoles()
        this.loading = false
    },
    methods: {
        getRoleColor(idx) {
            const colors = ["role-clr-blue", "role-clr-purple", "role-clr-teal", "role-clr-amber", "role-clr-rose"]
            return colors[idx % colors.length]
        },
        getRoleIcon(name) {
            if (!name) return "fas fa-user"
            const n = name.toLowerCase()
            if (n.includes("super")) return "fas fa-crown"
            if (n.includes("admin")) return "fas fa-user-shield"
            if (n.includes("manager")) return "fas fa-user-tie"
            if (n.includes("employee")) return "fas fa-user"
            if (n.includes("hr")) return "fas fa-users-cog"
            return "fas fa-user-tag"
        },
        closeForm() {
            this.showForm = false
            this.editMode = false
            this.editData = null
        },
        handleFormSuccess() {
            this.closeForm()
            this.fetchRoles()
        },
        enableEditing(role) {
            this.editMode = true
            this.editData = { ...role }
            this.showForm = true
            window.scrollTo({ top: 0, behavior: "smooth" })
        },
        async fetchRoles() {
            try {
                const res = await http.get("/user-roles")
                this.userRoles = res.data.result || res.data || []
            } catch (err) {
                console.log("error fetching user roles", err)
            }
        },
        async deleteRole(id) {
            if (!confirm("Are you sure you want to delete this role?")) return
            try {
                await http.delete(`/user-roles/${id}`)
                this.$toast.success("Role deleted successfully")
                this.userRoles = this.userRoles.filter((r) => r.id !== id)
            } catch (err) {
                console.log("error deleting role", err)
                this.$toast.error("Error deleting role")
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.roles-page {
    animation: rolesFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes rolesFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== HEADER ========== */
.roles-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 12px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}
.roles-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.roles-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.roles-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.roles-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}
.roles-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover { background: $primaryDark; }
}

/* ========== LOADING / EMPTY ========== */
.roles-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span { font-size: 13px; color: $gray400; font-weight: 500; }
}
.roles-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: rolesSpin 0.7s linear infinite;
}
@keyframes rolesSpin { to { transform: rotate(360deg); } }

.roles-empty {
    text-align: center;
    padding: 64px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 { font-size: 14px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 12px; color: $gray400; margin: 0; }
}
.roles-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i { font-size: 20px; color: $gray300; }
}

/* ========== CARD GRID ========== */
.roles-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}
.role-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    padding: 16px;
    box-shadow: $shadowSm;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: roleSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1) both;
    transition: all $transitionFast;

    &:hover {
        border-color: $gray200;
        box-shadow: $shadowMd;
        transform: translateY(-1px);
    }
}
@keyframes roleSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}
.role-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}
.role-clr-blue { background: #eff6ff; color: #2563eb; }
.role-clr-purple { background: #f5f3ff; color: #7c3aed; }
.role-clr-teal { background: #f0fdfa; color: #0d9488; }
.role-clr-amber { background: #fffbeb; color: #d97706; }
.role-clr-rose { background: #fff1f2; color: #e11d48; }

.role-card-body {
    flex: 1;
    min-width: 0;
}
.role-card-name {
    font-size: 14px;
    font-weight: 700;
    color: $gray800;
    margin: 0 0 2px;
    line-height: 1.2;
}
.role-card-id {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
}
.role-card-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}
.role-action-btn {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $gray200;
    border-radius: 6px;
    font-size: 11px;
    cursor: pointer;
    transition: all $transitionFast;
    background: white;
}
.role-btn-edit {
    color: $primaryColor;
    &:hover { background: $primaryLight; border-color: rgba(0, 94, 137, 0.15); }
}
.role-btn-delete {
    color: $danger;
    &:hover { background: #fef2f2; border-color: rgba(220, 38, 38, 0.15); }
}

/* ========== RESPONSIVE ========== */
@media (max-width: 992px) {
    .roles-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 480px) {
    .roles-grid { grid-template-columns: 1fr; }
    .role-card { padding: 12px; }
}
</style>
